import { observable, action, runInAction, makeObservable } from 'mobx';
import { Api, ApiAuthority } from '@/feature/ucenter/api/model/api'
import { request } from '@/framework/network/request'

export default class ApiStore {
  loading: boolean = false;
  list: Api[] = [];
  pagination: any = {
    pageSizeOptions: ['10', '20', '30', '50', '100'],
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: string) => `共 ${total} 条记录`
  };
  search: any = {};

  // edit
  apps: any[] = [];
  editing: boolean = false;
  saving: boolean = false;
  api: Api = new Api();

  // authority
  setting: boolean = false;
  authorities: ApiAuthority[] = [];

  // authority edit
  authorityEditing: boolean = false;
  authority: ApiAuthority = new ApiAuthority();

  fetchList = async (page = 0, size = 10, search: any = {}) => {
    runInAction(() => {
      this.loading = true
    })
    const res = await request.get('/u/apis', { ...search, page, size })
    runInAction(() => {
      this.list = res.data.content
      this.pagination = {
        ...this.pagination,
        total: res.data.totalElements,
        current: res.data.number + 1,
        pageSize: res.data.size,
      }
      this.loading = false
    })
  };

  fetchApps = async () => {
    runInAction(() => {
      this.loading = true
    })
    const res = await request.get('/u/apps')
    runInAction(() => {
      this.apps = res.data
      this.loading = false
    })
  };

  fetchApi = async (id?: number) => {
    runInAction(() => {
      this.loading = true
    })
    if (id) {
      const res = await request.get(`/u/api/${id}`)
      runInAction(() => {
        this.api = res.data
        this.loading = false
      })
    } else {
      runInAction(() => {
        this.api = new Api()
        this.loading = false
      })
    }
  };

  saveApi = async (api: Api) => {
    runInAction(() => {
      this.saving = true
    })
    if (api.id) {
      await request.put(`/u/api/${api.id}`, api)
    } else {
      await request.post(`/u/api`, api)
    }
    runInAction(() => {
      this.saving = false
    })
    await this.fetchList(this.pagination.current - 1, this.pagination.pageSize, this.search)
  };

  delete = async (id: number) => {
    runInAction(() => {
      this.loading = true
    })
    await request.delete(`/u/api/${id}`)
    runInAction(() => {
      this.loading = false
    })
    await this.fetchList(this.pagination.current - 1, this.pagination.pageSize, this.search)
  };

  startEdit = () => {
    runInAction(() => {
      this.editing = true
    })
  };

  finishEdit = () => {
    runInAction(() => {
      this.editing = false
    })
  };

  // ---------- authority ----------
  fetchAuthorities = async (id: number) => {
    runInAction(() => {
      this.loading = true
    })
    const res = await request.get(`/u/apiAuthorities`, { size: 9999, apiId: id })
    runInAction(() => {
      this.loading = false
      this.authorities = res.data.content
    })
  };

  startSetting = () => {
    runInAction(() => {
      this.setting = true
    })
  };

  finishSetting = () => {
    runInAction(() => {
      this.setting = false
    })
  };

  // ---------- authority edit ----------
  fetchAuthority = async (id?: number) => {
    runInAction(() => {
      this.loading = true
    })
    if (id) {
      const res = await request.get(`/u/apiAuthority/${id}`)
      runInAction(() => {
        this.authority = res.data
        this.loading = false
      })
    } else {
      runInAction(() => {
        this.authority = new ApiAuthority()
        this.loading = false
      })
    }
  };

  saveAuthority = async (authority: ApiAuthority) => {
    runInAction(() => {
      this.saving = true
    })
    if (authority.id) {
      await request.put(`/u/apiAuthority/${authority.id}`, authority)
    } else {
      await request.post('/u/apiAuthority', authority)
    }
    runInAction(() => {
      this.saving = false
    })
    await this.fetchAuthorities(this.api.id!)
  };

  generateKey = async (id: number) => {
    runInAction(() => {
      this.loading = true
    })
    const authority = this.authorities.find(item => item.id === id)
    if (authority) {
      await request.put(`/u/apiAuthority:generate/${id}/encrypted/${authority.encrypted}`, {})
    }
    runInAction(() => {
      this.loading = false
    })
  };

  deleteAuthority = async (id: number) => {
    runInAction(() => {
      this.loading = true
    })
    await request.delete(`/u/apiAuthority/${id}`)
    runInAction(() => {
      this.loading = false
    })
    await this.fetchAuthorities(this.api.id!)
  };

  startAuthorityEdit = () => {
    runInAction(() => {
      this.authorityEditing = true
    })
  };

  finishAuthorityEdit = () => {
    runInAction(() => {
      this.authorityEditing = false
    })
  };

  constructor() {
    makeObservable(this, {
      loading: observable,
      list: observable,
      pagination: observable,
      search: observable,
      apps: observable,
      editing: observable,
      saving: observable,
      api: observable,
      setting: observable,
      authorities: observable,
      authorityEditing: observable,
      authority: observable,
      fetchList: action,
      fetchApps: action,
      fetchApi: action,
      saveApi: action,
      delete: action,
      startEdit: action,
      finishEdit: action,
      fetchAuthorities: action,
      startSetting: action,
      finishSetting: action,
      fetchAuthority: action,
      saveAuthority: action,
      generateKey: action,
      deleteAuthority: action,
      startAuthorityEdit: action,
      finishAuthorityEdit: action
    });
  }
}

export interface ApiProps {
  apiStore?: ApiStore
}
